import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  FiChevronLeft,
  FiChevronRight,
  FiImage,
  FiRefreshCw,
  FiTrash2,
  FiUpload,
  FiX,
} from "react-icons/fi";
import { NumberInput, Radio } from "../../../../../Components";
import {
  InsetLeft,
  InsetOff,
  InsetRight,
  SideAB,
} from "../../../../../assets/icons/index";
import {
  MAX_ASSIGNED_LOGOS,
  addLogoAssignment,
  getAssignedLogoEntries,
  getLogoAssetName,
  moveLogoAssignment,
  reorderLogoAssignment,
  removeLogoAssignment,
  replaceLogoAssignment,
  updateLogoAssignment,
} from "./logoLayoutUtils";
import LayoutDisclosure from "../LayoutDisclosure";
import ColumnWidthPresets from "../ColumnWidthPresets";
import "./LogoManager.css";

const LogoManager = ({
  items,
  options,
  logoLibrary,
  currentBlockIdx,
  onItemsChange,
  onLayoutChange,
  onOptionChange,
  onSaveOptions,
  onUploadFile,
  onDeleteAsset,
  isCard,
}) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [replaceMode, setReplaceMode] = useState(false);
  const [filterQuery, setFilterQuery] = useState("");
  const draggedIndexRef = useRef(null);
  const assignedListRef = useRef(null);
  const selectedControlsRef = useRef(null);
  const uploadInputRef = useRef(null);
  const [assignedScroll, setAssignedScroll] = useState({
    max: 0,
    value: 0,
    thumbWidth: 32,
  });

  const assignedEntries = useMemo(() => getAssignedLogoEntries(items), [items]);
  const selectedEntry = assignedEntries[selectedIndex];
  const selectedLogo = selectedEntry?.logo;

  const filteredLibrary = useMemo(() => {
    const query = filterQuery.trim().toLowerCase();
    if (!query) return logoLibrary || [];
    return (logoLibrary || []).filter((logo) =>
      getLogoAssetName(logo).toLowerCase().includes(query),
    );
  }, [filterQuery, logoLibrary]);

  useEffect(() => {
    if (!assignedEntries.length) {
      setSelectedIndex(0);
      return;
    }
    if (selectedIndex >= assignedEntries.length) {
      setSelectedIndex(assignedEntries.length - 1);
    }
  }, [assignedEntries.length, selectedIndex]);

  useEffect(() => {
    const list = assignedListRef.current;
    if (!list) return undefined;

    const syncScrollbar = () => {
      const max = Math.max(list.scrollWidth - list.clientWidth, 0);
      const thumbWidth = max
        ? Math.max(32, (list.clientWidth / list.scrollWidth) * list.clientWidth)
        : list.clientWidth;
      setAssignedScroll({
        max,
        value: Math.min(list.scrollLeft, max),
        thumbWidth,
      });
    };

    syncScrollbar();
    const resizeObserver =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(syncScrollbar);
    resizeObserver?.observe(list);
    window.addEventListener("resize", syncScrollbar);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener("resize", syncScrollbar);
    };
  }, [assignedEntries.length]);

  useEffect(() => {
    const handlePreviewSelection = (event) => {
      if (event.detail?.blockIndex !== currentBlockIdx) return;
      const index = assignedEntries.findIndex(
        ({ logo }) =>
          (logo.row || 0) === event.detail.row &&
          (logo.position || 0) === event.detail.position,
      );
      if (index >= 0) setSelectedIndex(index);
    };
    window.addEventListener("scrollx:logo-select", handlePreviewSelection);
    return () =>
      window.removeEventListener("scrollx:logo-select", handlePreviewSelection);
  }, [assignedEntries, currentBlockIdx]);

  const revealSelectedControls = () => {
    window.requestAnimationFrame(() => {
      selectedControlsRef.current?.scrollIntoView?.({
        behavior: "smooth",
        block: "nearest",
      });
    });
  };

  const commitLayoutResult = (result) => {
    onItemsChange(result.items);
    onLayoutChange(result.layout);
    setSelectedIndex(Math.max(result.selectedIndex ?? 0, 0));
    revealSelectedControls();
  };

  const assignAsset = (asset) => {
    if (!asset) return;
    if (replaceMode && selectedEntry) {
      onItemsChange(replaceLogoAssignment(items, selectedIndex, asset));
      setReplaceMode(false);
      setLibraryOpen(false);
      revealSelectedControls();
      return;
    }
    commitLayoutResult(addLogoAssignment(items, asset));
    setReplaceMode(false);
    setLibraryOpen(false);
  };

  const requestUpload = (file) => {
    if (!file) return;
    onUploadFile(file, assignAsset);
  };

  const updateSelectedLogo = (patch) => {
    onItemsChange(updateLogoAssignment(items, selectedIndex, patch));
  };

  const removeSelectedLogo = () => {
    commitLayoutResult(removeLogoAssignment(items, selectedIndex));
  };

  const moveSelectedLogo = (direction, index = selectedIndex) => {
    commitLayoutResult(moveLogoAssignment(items, index, direction));
  };

  const reorderSelectedLogo = (targetIndex) => {
    const draggedIndex = draggedIndexRef.current;
    if (draggedIndex === null || draggedIndex === targetIndex) return;
    commitLayoutResult(reorderLogoAssignment(items, draggedIndex, targetIndex));
  };

  const openLibrary = (shouldReplace = false) => {
    setReplaceMode(shouldReplace);
    setLibraryOpen(true);
  };

  return (
    <section className="logo-manager" aria-label="Logo placement">
      <div className="logo-manager__section-heading">
        <div className="logo-manager__assigned-heading-copy">
          <h3>Assigned Logos</h3>
          <p>{assignedEntries.length} of {MAX_ASSIGNED_LOGOS} logos</p>
        </div>
        <div className="logo-manager__heading-actions">
          <button
            type="button"
            aria-label="Replace selected logo"
            data-tooltip="Replace"
            disabled={!selectedLogo}
            onClick={() => openLibrary(true)}
          >
            <FiRefreshCw />
          </button>
          <button
            type="button"
            className="is-remove"
            aria-label="Remove selected logo from block"
            data-tooltip="Delete"
            disabled={!selectedLogo}
            onClick={removeSelectedLogo}
          >
            <FiTrash2 />
          </button>
        </div>
      </div>

      {assignedEntries.length ? (
        <div className="logo-manager__assigned-strip">
          <div
            ref={assignedListRef}
            className="logo-manager__assigned-list"
            onScroll={(event) => {
              const value = event.currentTarget.scrollLeft;
              setAssignedScroll((current) => ({
                ...current,
                value,
              }));
            }}
          >
            {assignedEntries.map(({ logo }, index) => (
              <div
                key={`${logo.row}-${logo.position}-${logo.key || logo.url || index}`}
                className={`logo-manager__assigned-card ${selectedIndex === index ? "is-selected" : ""}`}
                onClick={() => setSelectedIndex(index)}
                role="button"
                tabIndex={0}
                draggable
                aria-roledescription="draggable logo"
                aria-label={`Logo ${index + 1}: ${getLogoAssetName(logo)}. Drag to reorder`}
                title="Drag to reorder"
                aria-current={selectedIndex === index ? "true" : undefined}
                onDragStart={(event) => {
                  draggedIndexRef.current = index;
                  setSelectedIndex(index);
                  event.dataTransfer.effectAllowed = "move";
                }}
                onDragOver={(event) => {
                  event.preventDefault();
                  event.dataTransfer.dropEffect = "move";
                }}
                onDrop={(event) => {
                  event.preventDefault();
                  reorderSelectedLogo(index);
                  draggedIndexRef.current = null;
                }}
                onDragEnd={() => {
                  draggedIndexRef.current = null;
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    setSelectedIndex(index);
                  }
                }}
              >
                <span className="logo-manager__assigned-thumb logo-manager__thumb--neutral">
                  <img src={logo.thumbnail || logo.url} alt="" />
                </span>
                <span className="logo-manager__assigned-actions">
                  <button
                    type="button"
                    aria-label={`Move ${getLogoAssetName(logo)} left`}
                    disabled={index === 0}
                    onClick={(event) => {
                      event.stopPropagation();
                      moveSelectedLogo(-1, index);
                    }}
                  >
                    <FiChevronLeft />
                  </button>
                  <button
                    type="button"
                    aria-label={`Move ${getLogoAssetName(logo)} right`}
                    disabled={index === assignedEntries.length - 1}
                    onClick={(event) => {
                      event.stopPropagation();
                      moveSelectedLogo(1, index);
                    }}
                  >
                    <FiChevronRight />
                  </button>
                </span>
              </div>
            ))}
          </div>
          {assignedScroll.max > 0 && (
            <input
              className="logo-manager__assigned-scrollbar"
              type="range"
              min="0"
              max={assignedScroll.max}
              step="1"
              value={Math.min(assignedScroll.value, assignedScroll.max)}
              aria-label="Scroll assigned logos"
              style={{
                "--logo-scroll-thumb-width": `${assignedScroll.thumbWidth}px`,
              }}
              onChange={(event) => {
                const value = Number(event.target.value);
                if (assignedListRef.current) {
                  assignedListRef.current.scrollLeft = value;
                }
                setAssignedScroll((current) => ({ ...current, value }));
              }}
            />
          )}
        </div>
      ) : (
        <div className="logo-manager__empty">
          <FiImage />
          <strong>No logos assigned</strong>
          <span>Add a logo and it will appear in the preview automatically.</span>
        </div>
      )}

      <div className="logo-manager__primary-actions">
        <button
          type="button"
          className="logo-manager__upload-button"
          disabled={assignedEntries.length >= MAX_ASSIGNED_LOGOS}
          onClick={() => uploadInputRef.current?.click()}
        >
          <FiUpload /> Upload New Logo
        </button>
        <button
          type="button"
          disabled={assignedEntries.length >= MAX_ASSIGNED_LOGOS}
          onClick={() => openLibrary(false)}
        >
          <FiImage /> Choose from Library
        </button>
        <input
          ref={uploadInputRef}
          className="logo-manager__hidden-input"
          type="file"
          accept="image/png,image/jpeg,.png,.jpg,.jpeg"
          onChange={(event) => {
            requestUpload(event.target.files?.[0]);
            event.target.value = "";
          }}
        />
      </div>

      {libraryOpen && (
        <div className="logo-manager__library">
          <div className="logo-manager__section-heading">
            <div>
              <h3>{replaceMode ? "Replace Selected Logo" : "Logo Library"}</h3>
              <p>{replaceMode ? "Choose the replacement asset." : "Click an asset to add it."}</p>
            </div>
            <button
              type="button"
              className="logo-manager__close-library"
              aria-label="Close logo library"
              title="Close"
              onClick={() => {
                setLibraryOpen(false);
                setReplaceMode(false);
              }}
            >
              <FiX />
            </button>
          </div>
          <input
            className="logo-manager__search"
            type="search"
            placeholder="Search logo library"
            value={filterQuery}
            onChange={(event) => setFilterQuery(event.target.value)}
          />
          <div className="logo-manager__library-grid">
            {filteredLibrary.map((logo, index) => {
              const isSelected =
                selectedLogo &&
                (selectedLogo.key
                  ? selectedLogo.key === logo.key
                  : (selectedLogo.url || selectedLogo.thumbnail) ===
                    (logo.url || logo.thumbnail));
              return (
                <div
                  key={logo._id || logo.key || logo.url || logo.thumbnail || index}
                  className={`logo-manager__library-card ${isSelected ? "is-selected" : ""}`}
                >
                  <button type="button" onClick={() => assignAsset(logo)}>
                    <span className="logo-manager__library-thumb logo-manager__thumb--neutral">
                      <img src={logo.thumbnail || logo.url} alt="" />
                    </span>
                    <strong title={getLogoAssetName(logo)}>{getLogoAssetName(logo)}</strong>
                  </button>
                  {logo?._id && (
                    <button
                      type="button"
                      className="logo-manager__delete-asset"
                      aria-label={`Delete ${getLogoAssetName(logo)} from library`}
                      title="Delete from Library"
                      onClick={() => onDeleteAsset(logo)}
                    >
                      <FiTrash2 />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
          {!filteredLibrary.length && (
            <div className="logo-manager__empty">No matching logos found.</div>
          )}
        </div>
      )}

      {selectedLogo && (
        <div
          className="logo-manager__transform-controls"
          role="group"
          aria-label="Logo position and scale"
          ref={selectedControlsRef}
        >
          <div className="logo-manager__transform-field">
            <div className="logo-manager__transform-label">Scale</div>
            <NumberInput
              postLabel="%"
              value={selectedLogo.scale ?? 80}
              min={50}
              max={100}
              onChange={(scale) => updateSelectedLogo({ scale })}
            />
          </div>
          <div className="logo-manager__transform-field">
            <div className="logo-manager__transform-label">Position X</div>
            <NumberInput
              postLabel="px"
              value={selectedLogo.offset_x || 0}
              min={-100}
              max={100}
              onChange={(offset_x) => updateSelectedLogo({ offset_x })}
            />
          </div>
          <div className="logo-manager__transform-field">
            <div className="logo-manager__transform-label">Position Y</div>
            <NumberInput
              postLabel="px"
              value={selectedLogo.offset_y || 0}
              min={-100}
              max={100}
              onChange={(offset_y) => updateSelectedLogo({ offset_y })}
            />
          </div>
        </div>
      )}

      <LayoutDisclosure title="Structure">
        <div className="optionSection">
          <div className="optionSectionLabel">Block Inset</div>
          <div className="optionSectionContent BLock-inset BLock-inset-nameonly">
            <Radio
              label={options?.block_inset}
              options={[
                { icon: InsetOff, value: "default" },
                { icon: InsetLeft, value: "left" },
                { icon: InsetRight, value: "right" },
                { icon: SideAB, value: "sidebyside" },
              ]}
              value={options?.block_inset}
              onChange={(value) => {
                onOptionChange("block_inset", value);
                onSaveOptions();
              }}
              withIcon
            />
          </div>
        </div>
      </LayoutDisclosure>

      <LayoutDisclosure title="Spacing">
        <div
          className="logo-manager__block-settings"
          role="group"
          aria-label="Block spacing settings"
        >
        <div className="optionSection">
          <div className="optionSectionLabel">Width</div>
          <div className="optionSectionContent columnWidthControls">
            <ColumnWidthPresets
              value={Number(options?.column_width) || 100}
              minWidth={20}
              maxWidth={100}
              onChange={(value) => {
                onOptionChange("column_width", value);
                onSaveOptions();
              }}
            />
            <NumberInput
              postLabel="%"
              value={Number(options?.column_width) || 100}
              min={20}
              max={100}
              onChange={(value) => onOptionChange("column_width", value)}
              onFocusOut={onSaveOptions}
            />
          </div>
        </div>

        {!isCard && (
          <>
            <div className="optionSection">
              <div className="optionSectionLabel">Margin</div>
              <div className="optionSectionContent">
                <div className="label-2 label-nameonly">Vertical</div>
                <NumberInput
                  value={options?.margin_vertical ?? 0}
                  min={0}
                  max={50}
                  step={10}
                  postLabel="%"
                  onChange={(value) =>
                    onOptionChange("margin_vertical", value)
                  }
                  onFocusOut={onSaveOptions}
                />
              </div>
            </div>
            <div className="optionSection">
              <div className="optionSectionContent2">
                <div className="optionSectionLabel">Bottom</div>
                <NumberInput
                  value={options?.extra_lines_below}
                  min={0}
                  max={25}
                  onChange={(value) =>
                    onOptionChange("extra_lines_below", value)
                  }
                  onFocusOut={onSaveOptions}
                />
              </div>
            </div>
          </>
        )}
        </div>
      </LayoutDisclosure>
    </section>
  );
};

export default LogoManager;
